import React, { useState, useMemo, useCallback } from 'react';
import './table.css';
// Generic type for table data
export interface TableColumn<T> {
  accessor: keyof T | ((row: T) => any);
  header: React.ReactNode;
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enableColumnResize?: boolean; // For future extension
  enableHide?: boolean; // For future extension
  cellRenderer?: (value: any, row: T) => React.ReactNode;
  aggregationFn?: 'sum' | 'count' | 'avg' | 'min' | 'max';
  width?: number;
  minWidth?: number;
  maxWidth?: number;
}

export interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  initialSorting?: { column: keyof T; direction: 'asc' | 'desc' }[];
  initialGlobalFilter?: string;
  initialPagination?: { pageIndex: number; pageSize: number };
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  enableRowSelection?: boolean;
  enableRowExpansion?: boolean;
  onRowClick?: (row: T) => void;
  onSelectionChange?: (selectedRows: T[]) => void;
  expandableRowRender?: (row: T) => React.ReactNode;
  pageSizeOptions?: number[];
}

/** --- The CustomTable component --- **/
function CustomTable<T extends object>({
  data,
  columns,
  initialSorting = [],
  initialGlobalFilter = '',
  initialPagination = { pageIndex: 0, pageSize: 10 },
  enableSorting = true,
  enableFiltering = true,
  enablePagination = true,
  enableRowSelection = false,
  enableRowExpansion = false,
  onRowClick,
  onSelectionChange,
  expandableRowRender,
  pageSizeOptions = [10, 20, 50, 100],
}: TableProps<T>) {
  const [sorting, setSorting] = useState(initialSorting);
  const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter);
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set());
  const [expandedRowIds, setExpandedRowIds] = useState<Set<number>>(new Set());

  const filteredData = useMemo(() => {
    if (!enableFiltering || !globalFilter.trim()) return data;

    const lowerFilter = globalFilter.toLowerCase();
    return data.filter(row =>
      columns.some(col => {
        const val = typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor as keyof T];
        if (val == null) return false;
        return String(val).toLowerCase().includes(lowerFilter);
      })
    );
  }, [data, globalFilter, columns, enableFiltering]);

  // Sort the filtered data
  const sortedData = useMemo(() => {
    if (!enableSorting || sorting.length === 0) return filteredData;

    const sorted = [...filteredData];
    sorted.sort((a, b) => {
      for (const sortConfig of sorting) {
        const { column: colKey, direction } = sortConfig;
        const aVal = a[colKey];
        const bVal = b[colKey];

        // Support numbers and strings
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return direction === 'asc' ? -1 : 1;
        if (bVal == null) return direction === 'asc' ? 1 : -1;

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          if (aVal < bVal) return direction === 'asc' ? -1 : 1;
          if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        } else {
          // string compare
          const aStr = String(aVal).toLowerCase();
          const bStr = String(bVal).toLowerCase();
          if (aStr < bStr) return direction === 'asc' ? -1 : 1;
          if (aStr > bStr) return direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });
    return sorted;
  }, [filteredData, sorting, enableSorting]);

  // Paginate sorted data
  const pageCount = enablePagination ? Math.ceil(sortedData.length / pagination.pageSize) : 1;

  const paginatedData = useMemo(() => {
    if (!enablePagination) return sortedData;
    const start = pagination.pageIndex * pagination.pageSize;
    return sortedData.slice(start, start + pagination.pageSize);
  }, [sortedData, pagination, enablePagination]);

  // Toggle row selection
  const toggleRowSelection = useCallback(
    (globalIndex: number) => {
      if (!enableRowSelection) return;

      setSelectedRowIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(globalIndex)) newSet.delete(globalIndex);
        else newSet.add(globalIndex);

        if (onSelectionChange) {
          const selectedRows = Array.from(newSet).map(i => sortedData[i]);
          onSelectionChange(selectedRows);
        }

        return newSet;
      });
    },
    [enableRowSelection, onSelectionChange, sortedData]
  );

  // Toggle row expansion
  const toggleRowExpansion = useCallback(
    (globalIndex: number) => {
      if (!enableRowExpansion) return;

      setExpandedRowIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(globalIndex)) newSet.delete(globalIndex);
        else newSet.add(globalIndex);
        return newSet;
      });
    },
    [enableRowExpansion]
  );

  // Handle sort header click
  const onHeaderClick = (key: string | keyof T, column: TableColumn<T>) => {
    if (!enableSorting || !column.enableSorting) return;

    setSorting(curr => {
      const exists = curr.find(s => s.column === key);
      if (exists) {
        // toggle direction or remove if desc
        if (exists.direction === 'asc') {
          return [{ column: key as keyof T, direction: 'desc' }];
        } else {
          return [];
        }
      } else {
        return [{ column: key as keyof T, direction: 'asc' }];
      }
    });

    // Reset to first page when sorting changes
    setPagination(curr => ({ ...curr, pageIndex: 0 }));
  };

  return (
    <div className={"custom-table-container"}>

      {/* Search input aligned right */}
      {enableFiltering && (
        <div className={"custom-table-header"}>
          <input
            type="text"
            placeholder="Search..."
            value={globalFilter}
            onChange={e => setGlobalFilter(e.target.value)}
            aria-label="Global search"
            className={"global-filter"}
          />
        </div>
      )}

      <table role="table" className={"table"}>
        <thead>
          <tr>
            {enableRowSelection && <th></th>}
            {enableRowExpansion && <th></th>}
            {columns.map((col, colIndex) => {
              const key =
                typeof col.accessor === 'string' ? col.accessor.toString() : `col-${colIndex}`;
              return (
                <th
                  key={key}
                  onClick={() => onHeaderClick(key, col)}
                  className={col.enableSorting ? "sortable" : undefined}
                  aria-sort={
                    sorting.length > 0 && sorting[0].column === key
                      ? sorting[0].direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  tabIndex={col.enableSorting ? 0 : undefined}
                  onKeyDown={e => {
                    if (col.enableSorting && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onHeaderClick(key, col);
                    }
                  }}
                >
                  {col.header}
                  {sorting.length > 0 && sorting[0].column === key ? (
                    sorting[0].direction === 'asc' ? (
                      <span aria-hidden="true"> ▲</span>
                    ) : (
                      <span aria-hidden="true"> ▼</span>
                    )
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {paginatedData.map((row, rowIndex) => {
            const globalIndex = (pagination.pageIndex || 0) * (pagination.pageSize || 10) + rowIndex;
            return (
              <React.Fragment key={globalIndex}>
                <tr
                  onClick={() => (onRowClick ? onRowClick(row) : undefined)}
                  className={onRowClick ? "row-clickable" : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={e => {
                    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }}
                >
                  {enableRowSelection && (
                    <td className={"checkbox-cell"} onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedRowIds.has(globalIndex)}
                        onChange={() => toggleRowSelection(globalIndex)}
                        aria-label={`Select row ${globalIndex + 1}`}
                      />
                    </td>
                  )}
                  {enableRowExpansion && (
                    <td className={"expand-cell"} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => toggleRowExpansion(globalIndex)}
                        aria-label={
                          expandedRowIds.has(globalIndex) ? 'Collapse row' : 'Expand row'
                        }
                        className={"expand-button"}
                        type="button"
                      >
                        {expandedRowIds.has(globalIndex) ? '−' : '+'}
                      </button>
                    </td>
                  )}
                  {columns.map((col, colIndex) => {
                    const key =
                      typeof col.accessor === 'string'
                        ? col.accessor.toString()
                        : `col-${colIndex}`;
                    const value =
                      typeof col.accessor === 'function'
                        ? col.accessor(row)
                        : (row[col.accessor as keyof T] as any);
                    return (
                      <td key={key} className={"cell"}>
                        {col.cellRenderer ? col.cellRenderer(value, row) : String(value)}
                      </td>
                    );
                  })}
                </tr>

                {enableRowExpansion &&
                  expandedRowIds.has(globalIndex) &&
                  expandableRowRender && (
                    <tr>
                      <td
                        colSpan={
                          columns.length +
                          (enableRowSelection ? 1 : 0) +
                          (enableRowExpansion ? 1 : 0)
                        }
                        className={"expand-row-content"}
                      >
                        {expandableRowRender(row)}
                      </td>
                    </tr>
                  )}
              </React.Fragment>
            );
          })}
          {paginatedData.length === 0 && (
            <tr>
              <td
                colSpan={
                  columns.length + (enableRowSelection ? 1 : 0) + (enableRowExpansion ? 1 : 0)
                }
                style={{ textAlign: 'center', padding: '20px' }}
              >
                No data found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      {enablePagination && (
        <div className={"custom-table-pagination"} role="navigation" aria-label="Table pagination">
          <div>
            Page{' '}
            <strong>
              {pagination.pageIndex + 1} of {pageCount}
            </strong>
          </div>
          <div>
            <button
              onClick={() =>
                setPagination(curr => ({ ...curr, pageIndex: Math.max(curr.pageIndex - 1, 0) }))
              }
              disabled={pagination.pageIndex === 0}
              type="button"
            >
              Previous
            </button>{' '}
            <button
              onClick={() =>
                setPagination(curr => ({
                  ...curr,
                  pageIndex: Math.min(curr.pageIndex + 1, pageCount - 1),
                }))
              }
              disabled={pagination.pageIndex >= pageCount - 1}
              type="button"
            >
              Next
            </button>
          </div>
          <div>
            <label htmlFor="pageSizeSelect" className={"rows-per-page-label"}>
              Rows per page:
            </label>{' '}
            <select
              id="pageSizeSelect"
              value={pagination.pageSize}
              onChange={e => setPagination({ pageIndex: 0, pageSize: Number(e.target.value) || 10 })}
              aria-label="Rows per page"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomTable;
